import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  loadNotifications,
  syncUnreadCount,
  markNotificationRead,
  markAllNotificationsReadThunk,
  dismissNotification,
  selectNotifications,
  selectUnreadCount,
  selectCategoryCounts,
  selectNotificationsStatus,
} from "../store/slices/notificationsSlice";
import {
  NOTIFICATIONS_REFRESH_EVENT,
  requestNotificationsRefresh,
} from "../services/notificationsApi";

// Re-exported so callers can trigger a refresh without importing the service.
export { NOTIFICATIONS_REFRESH_EVENT, requestNotificationsRefresh };

const POLL_INTERVAL_MS = 20000;



export function useNotifications({ poll = false } = {}) {
  
  const dispatch = useAppDispatch();

  const notifications = useAppSelector(selectNotifications);
  const unreadCount = useAppSelector(selectUnreadCount);
  const categoryCounts = useAppSelector(selectCategoryCounts);
  const status = useAppSelector(selectNotificationsStatus);

  useEffect(() => {
    if (!poll) return;

    // Initial load.
    
    dispatch(loadNotifications(undefined));

    // Background poll — cheap unread-count check that pulls the list only on drift.
    const intervalId = window.setInterval(() => {
       dispatch(syncUnreadCount());
    }, POLL_INTERVAL_MS);

    // Immediate refresh when a notification-producing action fires.
    const handleRefresh = () => {
       dispatch(loadNotifications({ silent: true }));
    };
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);

    // Refresh when the tab regains focus/visibility.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
         dispatch(syncUnreadCount());
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [dispatch, poll]);

  return {
    notifications,
    unreadCount,
    categoryCounts,
    status,
    refresh: () => dispatch(loadNotifications({ silent: true })),
    markRead: (id) => dispatch(markNotificationRead(id)),
    markAllRead: () => dispatch(markAllNotificationsReadThunk()),
    dismiss: (id) => dispatch(dismissNotification(id)),
  };
}
